<template>
  <div 
    :class="{ 'border-blue-500 bg-blue-50': dragOver }" 
    class="py-6 px-4 rounded-lg shadow-sm border-4 border-dashed border-gray-300 transition-colors"
    @dragenter.prevent="dragOver = true"
    @dragover.prevent="dragOver = true" 
    @dragleave.prevent="dragOver = false"
    @drop.prevent="handleDrop"
  >
    <div>
      <!-- Completed Label -->
      <div v-if="isUploaded && modelValue" class="mb-4 text-center">
        <span
          class="text-green-700 bg-green-100 py-1 px-3 rounded-full custom-scale custom-fadeIn"
          >Completed</span
        >
      </div>

      <!-- Heading -->
      <h2
        :class="{ 
          'text-white': isUploaded && modelValue, // Optional: Adjust style if needed on complete
          'text-gray-900': !(isUploaded && modelValue)
        }"
        class="text-lg font-bold mb-2 text-center"
      >
        {{ heading }}
      </h2>
      <!-- Secondary heading -->
      <h3
         :class="{ 
          'text-white': isUploaded && modelValue, 
          'text-gray-600': !(isUploaded && modelValue)
        }"
        class="text-sm md:text-md text-center my-2"
      >
        {{ subheading }}
      </h3>

      <!-- Upload Success Message / File Name -->
      <p v-if="isUploaded && modelValue" class="text-green-700 text-center custom-fadeIn">
        Uploaded: <br /><span class="font-medium">{{ modelValue.name }}</span
        ><br />
        (Upload Successful)
      </p>

      <!-- Upload Area -->
      <div v-if="!(isUploaded && modelValue)">
        <!-- Mobile Camera/Upload Options -->
        <div v-if="isMobile && !cameraActive" class="flex space-x-2 mb-3">
          <button 
            @click="activateCamera()" 
            class="flex-1 py-2 px-3 bg-blue-50 border rounded-md flex items-center justify-center"
            type="button"
          >
            <Icon icon="mdi:camera" class="mr-2" />
            Take Photo
          </button>
          <button 
            :for="inputId"
            @click="triggerFileInput"
            class="flex-1 py-2 px-3 bg-gray-50 border rounded-md flex items-center justify-center"
            type="button"
          >
            <Icon icon="mdi:file-upload" class="mr-2" />
            Upload File
          </button>
        </div>
        
        <!-- Camera View -->
        <div v-if="cameraActive" class="mb-3">
          <video ref="videoRef" autoplay class="w-full rounded-md"></video>
          <div class="flex justify-between mt-2">
            <button 
              @click="cancelCamera" 
              class="bg-gray-500 text-white py-2 px-4 rounded-md"
              type="button"
            >
              Cancel
            </button>
            <button 
              @click="captureImage" 
              class="bg-blue-500 text-white py-2 px-4 rounded-md"
              type="button"
            >
              Capture
            </button>
          </div>
        </div>
        
        <!-- Standard File Upload (Default or Desktop) -->
        <div v-if="!cameraActive" class="mt-4">
          <!-- Hidden File Input -->
          <input
            type="file"
            class="hidden"
            :id="inputId"
            :accept="acceptedFileTypes"
            @change="handleFileChange"
            ref="fileInputRef"
            :capture="isMobileWithoutCameraAPI ? 'environment' : false"
          />
          
          <!-- Visible Label / Drop Zone -->
          <label
            v-if="!isMobile || (isMobile && !cameraSupported)"
            :for="inputId"
            class="flex flex-col items-center justify-center w-full h-24 md:h-32 p-4 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors text-center"
          >
             <Icon
                icon="mdi:cloud-upload-outline"
                class="text-2xl text-gray-500 mb-2"
              />
            <p class="text-gray-600 text-md md:text-lg">
              {{ fileLabel }}
            </p>
          </label>
        </div>
      </div>

       <!-- Error Message -->
      <p v-if="error" class="text-red-500 text-xs italic mt-2 text-center">{{ error }}</p>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { Icon } from '@iconify/vue';
import { useMediaQuery } from '@vueuse/core';

// Props
const props = defineProps({
  modelValue: {
    type: [File, null],
    default: null
  },
  id: {
    type: String,
    default: null
  },
  heading: {
    type: String,
    default: "Upload Document"
  },
  subheading: {
      type: String,
      default: "PDF, DOC, DOCX, JPG, JPEG, PNG are accepted."
  },
  acceptedFileTypes: {
    type: String,
    default: ".pdf,.doc,.docx,.jpg,.jpeg,.png"
  },
  error: { // Allow parent to pass validation errors
      type: String,
      default: ''
  },
  enableCamera: {
    type: Boolean,
    default: true
  }
});

// Emits
const emit = defineEmits(['update:modelValue', 'error', 'upload-start', 'upload-complete']);

// Internal State
const fileInputRef = ref(null);
const dragOver = ref(false);
const cameraActive = ref(false);
const videoRef = ref(null);
const canvasRef = ref(document.createElement('canvas'));
const stream = ref(null);
const isMobile = useMediaQuery('(max-width: 768px)');
const cameraSupported = ref(false);

// Check if device supports advanced camera API
onMounted(() => {
  // Check if the browser supports the MediaDevices API
  cameraSupported.value = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
});

// Clean up on component unmount
onBeforeUnmount(() => {
  stopCamera();
});

// Check if a file is considered uploaded (based on v-model)
const isUploaded = computed(() => props.modelValue instanceof File);

// For devices that don't support the MediaDevices API but are mobile
const isMobileWithoutCameraAPI = computed(() => {
  return isMobile.value && !cameraSupported.value;
});

// Dynamic label for the upload area
const fileLabel = computed(() => {
  return isMobile.value ? 'Tap to upload a file' : 'Click or drag file to upload';
});

// Generate a unique ID if not provided
const inputId = computed(() => props.id || `document-upload-${Math.random().toString(36).substring(7)}`);

// Methods
const handleFileChange = (event) => {
  const files = event.target.files;
  if (files && files.length > 0) {
    // Signal upload start
    emit('upload-start');
    
    // Simulate processing time for better UX
    setTimeout(() => {
      // Update the v-model
      emit('update:modelValue', files[0]);
      emit('upload-complete');
    }, 800); // Slight delay to show processing
  } else {
     emit('update:modelValue', null); // Clear if selection cancelled
  }
};

const handleDrop = (event) => {
  dragOver.value = false;
  const files = event.dataTransfer.files;
  if (files && files.length > 0) {
    // Signal upload start
    emit('upload-start');
    
    // Check if the dropped file type is acceptable
    const file = files[0];
    const acceptedTypes = props.acceptedFileTypes.split(',').map(t => t.trim().toLowerCase());
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    const fileMimeType = file.type.toLowerCase();

    const isAccepted = acceptedTypes.some(type => 
      (type.startsWith('.') && fileExtension === type) || 
      (type.includes('/') && fileMimeType === type) ||
      (type === 'image/*' && fileMimeType.startsWith('image/')) ||
      (type === 'application/*' && fileMimeType.startsWith('application/'))
    );

    if (isAccepted) {
        setTimeout(() => {
          emit('update:modelValue', file);
          emit('upload-complete');
        }, 800); // Slight delay for better UX
    } else {
        console.warn('Dropped file type not accepted:', file.type, fileExtension);
        emit('update:modelValue', null); // Ensure model is cleared
        emit('error', 'File type not supported. Please upload a supported file type.');
        emit('upload-complete');
    }
  } else {
      emit('update:modelValue', null);
      emit('upload-complete');
  }
};

// Camera functions
const activateCamera = async () => {
  if (!props.enableCamera || !cameraSupported.value) return;
  
  try {
    // Signal camera activation
    emit('upload-start');
    
    stream.value = await navigator.mediaDevices.getUserMedia({ 
      video: { facingMode: 'environment' },
      audio: false 
    });
    
    cameraActive.value = true;
    
    // Wait for the component to update
    setTimeout(() => {
      if (videoRef.value) {
        videoRef.value.srcObject = stream.value;
      }
    }, 100);
  } catch (error) {
    console.error('Error accessing camera:', error);
    emit('error', 'Could not access camera. Please check camera permissions or try uploading a file instead.');
    emit('upload-complete');
  }
};

const stopCamera = () => {
  if (stream.value) {
    stream.value.getTracks().forEach(track => track.stop());
    stream.value = null;
  }
  cameraActive.value = false;
};

const cancelCamera = () => {
  stopCamera();
};

const captureImage = () => {
  if (!videoRef.value || !stream.value) return;
  
  const video = videoRef.value;
  const canvas = canvasRef.value;
  
  // Set canvas dimensions to match video
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  
  // Draw the video frame to canvas
  canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
  
  // Convert canvas to blob/file
  canvas.toBlob((blob) => {
    if (blob) {
      // Create a File from Blob
      const file = new File([blob], `camera_capture_${Date.now()}.jpg`, { type: 'image/jpeg' });
      
      // Update the model
      emit('update:modelValue', file);
      emit('upload-complete');
      
      // Stop the camera after capture
      stopCamera();
    }
  }, 'image/jpeg', 0.95); // High quality JPEG
};

// Trigger file input clicking
const triggerFileInput = () => {
  if (fileInputRef.value) {
    fileInputRef.value.click();
  }
};
</script>

<style scoped>
.custom-fadeIn {
  animation: fadeIn ease 0.5s;
}
@keyframes fadeIn {
  0% { opacity: 0; }
  100% { opacity: 1; }
}
/* Add any specific styles if needed */

/* Animation for camera activation */
.camera-enter-active,
.camera-leave-active {
  transition: opacity 0.3s ease-in-out;
}
.camera-enter-from,
.camera-leave-to {
  opacity: 0;
}
</style> 